import _, { assign } from 'lodash';
import Dates from '../../utils/dates';
import { replaceAccents } from '../../utils/utils';
import {
  urlProcessDefinitionId,
  urlTramitesDeAutorizacionesConMateriales,
  urlTramitesDeAutorizacionesGIA,
  urlTramitesDeAutorizacionesMedicacionUnica,
  urlTramitesDeAutorizacionesOdontodologia,
  urlTramitesDeAutorizacionesSinAutorizacion,
  urlTramitesDeAutorizacionesSinMateriales,
  urlTramitesDeAutorizacionesTrasladosProgramados,
} from '../../actions/urls';
import Session from '../../utils/session';
import { apiManagerRequest } from '../../axios';
import { AUTHORIZATION_TYPES } from '../../constants/authorization';
import { DataAuthorizationForm } from '../../documentation/Authorization';
import PresmedService from '../Presmed';
import { optionProcess } from '../../constants/sgi';

const getImageObjects = (files) => {
  const arrayFiles = [];

  if (!_.isEmpty(files)) {
    files.map((file) => {
      const newFile = {};
      newFile.fileName = `${file.name ? file.name.replace(/[\W_\r\n]+/g, ' ') : '_'}.${
        file.extension
      }`;
      newFile.sizeInBytes = file.size;
      newFile.base64Content = file.base64;
      arrayFiles.push(newFile);
    });
  }
  return arrayFiles;
};

const AuthorizationService = {
  /**
   * @param {DataAuthorizationForm} datos
   */
  saveGAUTMedicalAuthorization: async (datos) => {
    // migrado desde autorizacionesActions.js
    const {
      numeroDeAfiliado,
      prestacion,
      requiereMaterialesOProtesis: withMaterials,
      fechaDeProcedimiento,
      observacion,
      tipoAutorizacion,
      direccion,
      requiereMedicacion,
      pacienteInternado,
      ventanilla,
      fechaDeOrden,
      archivos,
      adjuntaPresupuesto,
      affiliatePrepaid: prepaga,
      coverageBenefit,
      exceptedBenefit,
    } = datos;

    const auth = Session.getAuth();
    const prestador = auth.id;
    const emailPrestador = auth.modelEspecifico.emailPrestador;
    const affiliateNumber = numeroDeAfiliado.split(' ');
    const contra = affiliateNumber[0];
    const inte = affiliateNumber[2];
    const attachBudget = adjuntaPresupuesto ? 1 : 0;
    const observationData =
      observacion && observacion !== ''
        ? replaceAccents(observacion).replace(/[\W_\r\n]+/g, ' ')
        : '';
    const hasBenefit = !_.isEmpty(prestacion) && prestacion.codigo;
    const medicalAuthorizationType = tipoAutorizacion;

    let url = null;

    let json = {
      prestador,
      emailPrestador: emailPrestador,
      lugarAtencionPrestador: direccion,
      adjuntaPresupuesto: attachBudget ? 1 : 0,
      requiereMedicacion: requiereMedicacion ? 1 : 0,
      pacienteInternado: pacienteInternado ? 1 : 0,
      ventanilla: ventanilla ? ventanilla : '',
      tipoAutorizacionMedica: medicalAuthorizationType,
      observacion: observationData,
      fechaOrden: fechaDeOrden,
      afiliado: {
        contra,
        inte,
        prepaga,
      },
      imagenes: getImageObjects(archivos),
    };

    if (withMaterials) {
      url = urlTramitesDeAutorizacionesConMateriales;
      json.fechaCirugia = fechaDeProcedimiento;
    } else if (medicalAuthorizationType === 'Medicacion unicamente') {
      url = urlTramitesDeAutorizacionesMedicacionUnica;
      json.fechaCirugia = fechaDeProcedimiento;
    } else if (hasBenefit) {
      json.prestacion = [prestacion];
      json.requiereMaterial = 0;

      switch (coverageBenefit) {
        case 'AP':
          if (exceptedBenefit) url = urlTramitesDeAutorizacionesSinMateriales;
          break;
        case 'NC':
          return {
            rechazo: true,
            jbpmProcess: 'Prestación no cubierta.',
          };
        case 'AM':
          url = urlTramitesDeAutorizacionesSinMateriales;
          break;
        case 'SA':
          url = urlTramitesDeAutorizacionesSinAutorizacion;
          break;
      }
    } else {
      url = urlTramitesDeAutorizacionesSinMateriales;
    }

    const { data: dataMedicalAutorization } = await apiManagerRequest.post(url, json);

    if (coverageBenefit) {
      dataMedicalAutorization.mod_cobert = coverageBenefit;
    }

    return dataMedicalAutorization;
  },
  /**
   * @param {DataAuthorizationForm} datos
   */
  saveGIAMedicalAuthorization: async (datos) => {
    const {
      numeroDeAfiliado,
      fechaDeOrden,
      archivos,
      affiliateFullName,
      pacienteInternado,
      coverageBenefit,
      prestacion,
      direccion,
      requiereMaterialesOProtesis,
      adjuntaPresupuesto,
      fechaDeProcedimiento,
    } = datos;

    const auth = Session.getAuth();
    // Afilmed: with zeroes , Presmed: without zeroes.
    const [contraAfilmed, x, inteAfilmed] = numeroDeAfiliado.split(' ');
    const contraPresmed = PresmedService.format(contraAfilmed);
    const intePresmed = PresmedService.format(inteAfilmed);
    let processDefinitionId = null;

    const { prepagaPresmed } = await PresmedService.getDataByAfilmedContract(contraAfilmed);

    try {
      const urlProcess = `${urlProcessDefinitionId}/Gestion-Autorizaciones`;
      const { data: dataProcessDefinition } = await apiManagerRequest.get(urlProcess);

      processDefinitionId = dataProcessDefinition.id;
    } catch (error) {
      processDefinitionId = 0;
    }

    const url = urlTramitesDeAutorizacionesGIA;
    const authProvince = auth.domicilio?.provincia?.descripcion ?? '';

    const generateDetailProcessText = () => {
      const divider = `|`;
      const contraInte = `${contraPresmed}/${intePresmed}`;
      const materialText = requiereMaterialesOProtesis ? `Requiere material ${divider} ` : '';
      const dateText = `${
        requiereMaterialesOProtesis
          ? `Fecha de procedimiento: ${Dates.get(fechaDeProcedimiento, 'DD/MM/YYYY').toFormat(
              'DD/MM/YYYY',
            )}`
          : `Fecha de orden: ${Dates.get(fechaDeOrden, 'DD/MM/YYYY').toFormat('DD/MM/YYYY')}`
      }`;
      const hospitalizedText = pacienteInternado ? `Paciente internado ${divider} ` : '';
      return `${materialText}${contraInte} ${divider} ${auth.id} ${divider} ${dateText} ${divider} ${hospitalizedText}${authProvince}`;
    };
    const detailProcess = generateDetailProcessText();

    const json = {
      giaBusinessData: {
        ordenMedica: {
          faltaOrden: 0,
          fechaOrden: Dates.get(fechaDeOrden, 'DD/MM/YYYY').toFormat('YYYY-MM-DD'),
          ...(requiereMaterialesOProtesis
            ? {
                fechaProcedimiento: Dates.get(fechaDeProcedimiento, 'DD/MM/YYYY').toFormat(
                  'YYYY-MM-DD',
                ),
              }
            : {}),
        },
        datosContacto: {
          emailContactoAfiliado: null,
          telefonoContactoAfiliado: null,
          faltaDatosContacto: true,
          taskName: 'Inicio de Trámite',
        },
        efector: {
          apeRazon: auth.apeRazonSocial,
          prestad: auth.id,
          tipo: auth.tipoPrestador.id,
          nombreAbre: auth.nombreAbre,
          lugar: {
            lugar: parseInt(direccion),
          },
        },
        rudiFiles: {
          pk: {
            tipoTramite: 'AUT',
          },
          origen: 'WEB',
          files: {
            items: archivos.map((file) => ({
              fileName: `${file.name ? file.name.replace(/[\W_\r\n]+/g, ' ') : '_'}.${
                file.extension
              }`,
              sizeInBytes: String(file.size),
              base64Content: file.base64,
            })),
          },
        },
        tramite: {
          idTramite: null,
          prepaga: prepagaPresmed,
          contra: contraPresmed,
          inte: intePresmed,
          usuario: optionProcess.user,
          medioId: 27,
          sucursalCarga: 1,
          idEstado: 1,
          atencionParticular: 0,
        },
        prestacionesData: {
          identificacionPrestacion: 0,
          requiereMaterial: requiereMaterialesOProtesis ? 1 : 0,
          requiereMedicacion: 0,
          requierePresupuesto: adjuntaPresupuesto ? 1 : 0,
          preexistencia: 0,
          carencia: 0,
          prestaciones: prestacion
            ? [
                {
                  codigo: prestacion.codigo,
                  cod_cobertura: prestacion.cod_cobertura,
                  carac: prestacion.carac,
                  descripcion: prestacion.descripcion,
                  tipo: prestacion.tipo,
                  nomenclador: prestacion.nomenclador,
                  cantidad: 1,
                  modalidadcobertura: prestacion.cod_cobertura,
                },
              ]
            : [],
        },
      },
      process: {
        processName: 'Gestion Integral Autorizaciones',
        workContext: 'AMB_AUTORIZACIONES',
        credencial: contraPresmed,
        afiliado: affiliateFullName,
        key: null,
        detalle: detailProcess,
        variables: {
          goToVariable: 'auditoria-administrativa',
          workContext: 'AMB_AUTORIZACIONES',
          estado: 'Pendiente',
          codigoPrestador: String(auth.id),
          processInfo: {
            id: '0',
            name: 'Gestion Integral Autorizaciones',
            description: 'Gestion Integral Autorizaciones',
            credencial: contraPresmed,
            registradoUsuario: optionProcess.specificUser,
            registradoNombre: optionProcess.description,
            detalle: detailProcess,
          },
        },
      },
      assignment: {
        provider: 'assignableAutoByPrestadSitTerapProxy',
        workContext: 'AMB_AUTORIZACIONES',
        processDefinitionId: processDefinitionId ?? 0, // 193759888, // PROCESS_DEFINITION_ID FROM ENDPOINT
        prestadorId: auth.id,
        afiliado: {
          inte: intePresmed,
          prepaga: prepagaPresmed,
          contra: contraPresmed,
        },
        tipoAutorizacionId: 'P',
      },
    };

    const { data: dataGIA } = await apiManagerRequest.post(url, json);

    return dataGIA;
  },
  /**
   * @param {DataAuthorizationForm} datos
   */
  saveMedical: async (datos) => {
    const isIntegralAuthorization =
      datos.tipoAutorizacion === AUTHORIZATION_TYPES.BENEFIT_PRACTICE_SURGERY &&
      (datos.requiereMaterialesOProtesis ||
        datos.adjuntaPresupuesto ||
        datos.coverageBenefit != 'SA');

    if (isIntegralAuthorization) {
      return AuthorizationService.saveGIAMedicalAuthorization(datos);
    }

    return AuthorizationService.saveGAUTMedicalAuthorization(datos);
  },
  /**
   * @param {DataAuthorizationForm} datos
   */
  saveScheduledTransfer: async (datos) => {
    const auth = Session.getAuth();
    const prestador = auth.id;
    const url = urlTramitesDeAutorizacionesTrasladosProgramados;
    const numeroDeAfiliado1 = datos.numeroDeAfiliado.split(' ');
    const prepaga = datos.affiliatePrepaid;
    const contra = numeroDeAfiliado1[0];
    const inte = numeroDeAfiliado1[2];
    let imagenes = [];

    if (!_.isEmpty(datos?.archivos)) {
      datos?.archivos.map((img) => {
        const image = {};
        image.fileName = `${img.name ? img.name.replace(/[\W_\r\n]+/g, ' ') : '_'}.${
          img.extension
        }`;
        image.sizeInBytes = img.size;
        image.base64Content = img.base64;
        imagenes.push(image);
      });
    }

    const json = {
      tramite: {
        prestador,
      },
      afiliado: {
        contra,
        inte,
        prepaga,
      },
      imagenes,
    };

    const { data } = await apiManagerRequest.post(url, json);

    return data;
  },
  /**
   * @param {DataAuthorizationForm} datos
   */
  saveDental: async (datos) => {
    const auth = Session.getAuth();
    const prestador = auth.id;
    let imagenes = [];
    const url = urlTramitesDeAutorizacionesOdontodologia;

    const numeroDeAfiliado2 = datos.numeroDeAfiliado.split(' ');
    const contra = numeroDeAfiliado2[0];
    const inte = numeroDeAfiliado2[2];
    const { ortodoncia, archivos, fechaDeOrden, direccion, affiliatePrepaid: prepaga } = datos;
    const observacion =
      datos.observacion && datos.observacion !== ''
        ? replaceAccents(datos.observacion).replace(/[\W_\r\n]+/g, ' ')
        : '';

    const hasImage = !_.isEmpty(archivos);
    if (!_.isEmpty(archivos)) {
      archivos.map((img) => {
        const image = {};
        image.fileName = `${img.name ? img.name.replace(/[\W_\r\n]+/g, ' ') : '_'}.${
          img.extension
        }`;
        image.sizeInBytes = img.size;
        image.base64Content = img.base64;
        imagenes.push(image);
      });
    }

    const tramite = {
      fechaOrden: fechaDeOrden && fechaDeOrden,
      prestador,
      lugarAtencionPrestador: direccion,
      observacion,
      ortodoncia: ortodoncia ? 1 : 0,
    };

    if (ortodoncia) {
      const { tratamiento, etapa } = datos;

      Object.assign(tramite, {
        tratamiento,
        etapa,
      });
    }

    const json = {
      tramite,
      afiliado: {
        contra,
        inte,
        prepaga,
      },
    };

    if (hasImage) {
      Object, assign(json, { imagenes });
    }

    const { data } = await apiManagerRequest.post(url, json);

    return data;
  },
};

export default AuthorizationService;
